import _ from 'lodash';
import browserHistory from '../history';
import AuthService from '../services/AuthService';
import {
  ADD_FAVORITE,
  ADD_NOTIFICATION,
  GET_INFO,
  GET_MENU_TREE,
  GET_MODAL_CONFIGS,
  GET_SEARCH_DATA,
  GET_USER,
  LOGIN_USER,
  LOGOUT_USER,
  REMOVE_FAVORITE,
  REMOVE_NOTIFICATION,
  REMOVE_USER_DATA,
  SET_BRANCH,
  SET_CODES,
  SET_SHOW_HELP,
  SET_USER,
  UPDATE_FAVORITELIST,
  UPDATE_FAVORITES,
  UPDATE_USER_DATA,
} from '../actions/AppActions';

const initialUserData = {
  favorites: [],
  favoriteList: {},
  layouts: {},
};

const INITIAL_STATE = {
  branch: {},
  branchOptions: {},
  branches: [],
  codes: [],
  config: {},
  mfaRequired: false,
  modalConfigs: {},
  notifications: [],
  searchData: {},
  user: { branch: {} },
  userData: { ...initialUserData },
  showHelp: JSON.parse(localStorage.getItem('showHelp')) || false,
};

const setUserHistory = user => {
  localStorage.setItem('user.page.history', JSON.stringify(_.get(user, 'pageHistory', [])));
};

/**
 * Group company to branches, used for branch selection
 */
const getBranchOptions = (user, branch) => {
  if (!user || !user.branches || !user.branches.length) {
    return {};
  }

  const branchOptions = {};

  const companies = _.keyBy(user.companies, 'id');

  user.branches.forEach(userBranch => {
    const company = companies[userBranch.companyId] || { name: 'Other' };
    if (!branchOptions[company.name]) {
      branchOptions[company.name] = [];
    }

    branchOptions[company.name].push({
      id: userBranch.id,
      name: userBranch.name,
      active: branch.id === userBranch.id,
      sortNum: userBranch.sortNum,
    });
  });

  return branchOptions;
};

function filterTree(menuTree, user, branch) {
  const filteredTree = _.cloneDeep(menuTree);

  const traverse = (menu = []) => {
    for (let index = menu.length - 1; index >= 0; index--) {
      const item = menu[index];

      if (item.menu) {
        const result = traverse(item.menu);
        if (!result) {
          menu.splice(index, 1);
        }
      } else {
        if (!AuthService.hasPermission(item.name, branch.id, user)) {
          menu.splice(index, 1);
        }
      }
    }

    if (!menu.length) {
      return false;
    }

    return true;
  };

  traverse(filteredTree);

  return filteredTree;
}

export default function (state = INITIAL_STATE, action) {
  if (action.error || _.get(action, 'payload.data.error')) {
    return state;
  }

  switch (action.type) {
    case GET_INFO: {
      const { user = {}, users, config, branch, notifications, token, codes, branches } = action.payload.data;

      if (token) {
        AuthService.setToken(token);
      }

      setUserHistory(user);

      return {
        ...state,
        config,
        branch,
        branchOptions: getBranchOptions(user, branch),
        notifications,
        user,
        users,
        codes,
        branches,
        userData: user.data || { ...initialUserData },
        mfaRequired: !user.mfaSecret && user.mfaRequired,
      };
    }

    case GET_USER: {
      const user = action.payload.data;
      setUserHistory(user);

      return {
        ...state,
        user,
        userData: user.data || { ...initialUserData },
        mfaRequired: !user.mfaSecret && user.mfaRequired,
      };
    }

    case GET_SEARCH_DATA:
      return {
        ...state,
        searchData: action.payload.data || {},
      };

    case GET_MENU_TREE:
      return {
        ...state,
        menuTree: action.payload.data,
        filteredTree: filterTree(action.payload.data, state.user, state.branch),
      };

    case GET_MODAL_CONFIGS:
      return {
        ...state,
        modalConfigs: action.payload.data,
      };

    case SET_USER: {
      const user = action.payload;
      setUserHistory(user);

      return {
        ...state,
        user,
        userData: user.data || { ...initialUserData },
        mfaRequired: !user.mfaSecret && user.mfaRequired,
      };
    }

    case SET_BRANCH:
      return {
        ...state,
        branch: action.payload,
        branchOptions: getBranchOptions(state.user, action.payload),
      };

    case SET_CODES:
      return {
        ...state,
        codes: action.payload,
      };

    case SET_SHOW_HELP:
      return { ...state, showHelp: action.value };

    case LOGIN_USER: {
      const { user, branch, codes } = action.payload;

      setUserHistory(user);
      return {
        ...state,
        user,
        codes,
        branch,
        branchOptions: getBranchOptions(user, branch),
        userData: user.data || { ...initialUserData },
        mfaRequired: !user.mfaSecret && user.mfaRequired,
      };
    }

    case LOGOUT_USER:
      AuthService.logout();
      browserHistory.push('/login');

      return {
        ...state,
        user: INITIAL_STATE.user,
        userData: { ...initialUserData },
        branchOptions: {},
      };

    case UPDATE_USER_DATA:
      return {
        ...state,
        userData: _.set(_.cloneDeep(state.userData), action.meta.key, action.meta.data), // set mutates so need to clone
      };

    case REMOVE_USER_DATA:
      return {
        ...state,
        userData: _.omit(_.cloneDeep(state.userData), action.meta.key),
      };

    case ADD_FAVORITE:
    case REMOVE_FAVORITE:
    case UPDATE_FAVORITES:
      return {
        ...state,
        userData: _.set(_.cloneDeep(state.userData), 'favoriteList', action.payload.data),
      };

    case UPDATE_FAVORITELIST:
      return {
        ...state,
        userData: _.set(_.cloneDeep(state.userData), 'favoriteList', action.payload.data),
      };

    case ADD_NOTIFICATION: {
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };
    }

    case REMOVE_NOTIFICATION: {
      if (action.payload.data.id === 'all') {
        return {
          ...state,
          notifications: [],
        };
      }

      const noteId = action.payload.data.id / 1; // Coercion
      const notifications = _.filter(state.notifications, note => note.id !== noteId);

      return {
        ...state,
        notifications,
      };
    }

    default:
      return state;
  }
}
